import { SVGWhiteIcon } from '@/common/svg-white-icon';
import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState, useCallback, type FC } from 'react';

import { SVGFileAPIs, VariantAPIs } from '@/api';
import type { Font } from '@/api/types/font';
import { BinIcon } from '@/common/bin-icon';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { CustomToast } from '@/common/custom-toast';
import { FilePicker } from '@/common/file-picker';
import { MissingFontsModal } from '@/common/missing-fonts-modal';
import { Modal } from '@/common/modal';

interface AddFrameModalProps {
    templateVersionId: number;
    isOpen: boolean;
    onFrameAdded?: () => void;
    prevCount: number;
    setIsOpen: (isOpen: boolean) => void;
}

export const AddFrameModal: FC<AddFrameModalProps> = ({
    isOpen,
    onFrameAdded,
    prevCount,
    setIsOpen,
    templateVersionId,
}) => {
    const [files, setFiles] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isAddingFrames, setIsAddingFrames] = useState<boolean>(false);
    const [isMissingFontModalOpen, setIsMissingFontModalOpen] = useState<boolean>(false);
    const [missingFonts, setMissingFonts] = useState<Font[]>([]);
    const [uploadedFonts, setUploadedFonts] = useState<Font[]>([]);
    const [progressMap, setProgressMap] = useState<{
        [key: string]: {
            isUploaded: boolean;
            error: boolean;
            progress: number;
            xhr: XMLHttpRequest;
            file: { id: number; file: string; missing_fonts: Font[] } | null;
        };
    }>({});
    const addFrames = async () => {
        if (files.length === 0) return;
        const fileIds = files
            .map((file) => ({ name: file.name, id: progressMap[file.name]?.file?.id }))
            .map((f, index) => ({
                template_version: templateVersionId,
                svg_file: f.id ?? -1,
                name: f.name,
            }));
        setIsAddingFrames(true);
        await VariantAPIs.createVariants(fileIds);
        CustomToast.success('Frames added successfully');
        setIsAddingFrames(false);
        setIsOpen(false);
        onFrameAdded?.();
    };

    const uploadFile = useCallback(
        async (file: File) => {
            if (progressMap[file.name]?.isUploaded || progressMap[file.name]?.xhr) {
                return;
            }
            const xhr = await SVGFileAPIs.uploadSVGFile(
                file,
                (event) => {
                    if (event.lengthComputable) {
                        const progress = Math.round((event.loaded / event.total) * 100);
                        setProgressMap((prev) => ({
                            ...prev,
                            [file.name]: {
                                progress,
                                error: false,
                                isUploaded: false,
                                xhr: xhr,
                                file: null,
                            },
                        }));
                    }
                },
                (event) => {
                    if (xhr.status === 201) {
                        setProgressMap((prev) => ({
                            ...prev,
                            [file.name]: {
                                progress: 100,
                                error: false,
                                isUploaded: true,
                                xhr: xhr,
                                file: JSON.parse(xhr.responseText),
                            },
                        }));
                    } else {
                        setProgressMap((prev) => ({
                            ...prev,
                            [file.name]: {
                                progress: 0,
                                error: true,
                                isUploaded: false,
                                xhr: xhr,
                                file: null,
                            },
                        }));
                        CustomToast.error('Failed to upload, double check the file and try again');
                    }
                },
            );
        },
        [progressMap],
    );

    useEffect(() => {
        const filesToUpload = files.filter((file) => !progressMap[file.name]?.isUploaded);
        const uploadFiles = async () => {
            for (const file of filesToUpload) {
                if (!progressMap[file.name]?.isUploaded) {
                    await uploadFile(file);
                }
            }
        };
        if (filesToUpload.length > 0) {
            uploadFiles();
        }
    }, [files, progressMap, uploadFile]);

    useEffect(() => {
        if (
            missingFonts.filter(
                (f) =>
                    !uploadedFonts.some(
                        (uf) => uf.family === f?.family && uf.weight === f?.weight && uf.style === f?.style,
                    ),
            ).length > 0
        ) {
            setIsMissingFontModalOpen(true);
        }
    }, [missingFonts, uploadedFonts]);

    const handleCancel = () => {
        setIsOpen(false);
        setFiles([]);
        setProgressMap({});
        for (const file of files) {
            const xhr = progressMap[file.name]?.xhr;
            if (xhr) xhr.abort();
        }
    };

    const handleAddFrames = async () => {
        let missingFonts = files
            .flatMap((file) => progressMap[file.name]?.file?.missing_fonts)
            .filter(
                (f) =>
                    !uploadedFonts.some(
                        (uf) => uf.family === f?.family && uf.weight === f?.weight && uf.style === f?.style,
                    ),
            );

        missingFonts = missingFonts.filter((value, index) => {
            const _value = JSON.stringify(value);
            return (
                index ===
                missingFonts.findIndex((obj) => {
                    return JSON.stringify(obj) === _value;
                })
            );
        });

        if (missingFonts.length > 0) {
            setMissingFonts(missingFonts.filter((font): font is Font => font !== undefined));
            return;
        }
        await addFrames();
        setFiles([]);
        setIsOpen(false);
    };

    const handleCancelFileUpload = (file: File) => {
        const xhr = progressMap[file.name]?.xhr;
        if (xhr) xhr.abort();
        setFiles(files.filter((f) => f.name !== file.name));
    };

    const handleDeleteFile = (file: File) => {
        setFiles(files.filter((f) => f.name !== file.name));
    };

    return (
        <div>
            <Modal isOpen={isOpen}>
                <div className="flex flex-col overflow-hidden absolute min-w-96 min-h-[50vh] max-h-[90vh] w-[38vw] max-w-[650px] bg-gray-150 shadow-xl rounded-xl transition-all duration-300 ease-in-out">
                    <div className="flex flex-row justify-between p-6 items-center">
                        <p className="text-xl text-white">Upload Frames</p>
                        <XMarkIcon
                            className="h-5 w-5 text-white hover:cursor-pointer hover:text-gray-30"
                            onClick={isLoading ? () => {} : () => setIsOpen(false)}
                        />
                    </div>
                    <hr className="bg-gray-100 border-gray-100" />
                    <div className="flex flex-col p-6 flex-1 min-h-0 overflow-hidden">
                        <FilePicker
                            fileTypes={{ 'image/svg+xml': ['.svg'] }}
                            icon={<SVGWhiteIcon className="w-14 h-14" />}
                            onFilesPicked={async (selectedFiles) => {
                                setFiles((prev) => [
                                    ...prev,
                                    ...selectedFiles.filter((file) => !prev.some((f) => f.name === file.name)),
                                ]);
                            }}
                        />
                        {files.length > 0 && (
                            <div className="flex flex-col flex-1 min-h-0 mt-6 overflow-y-auto gap-4 no-scrollbar">
                                {files.map((file, index) => {
                                    const fileSizeUploaded =
                                        (file.size * (progressMap[file.name]?.progress || 0)) / 100;
                                    const fileSizeUploadedWithUnit =
                                        fileSizeUploaded > 1024 * 1024
                                            ? `${(fileSizeUploaded / (1024 * 1024)).toFixed(2)} MB`
                                            : `${(fileSizeUploaded / 1024).toFixed(2)} KB`;
                                    const fileSizeTotal =
                                        file.size > 1024 * 1024
                                            ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                                            : `${(file.size / 1024).toFixed(2)} KB`;
                                    const progress = progressMap[file.name]?.progress || 0;
                                    const isUploaded = progressMap[file.name]?.isUploaded || false;
                                    const error = progressMap[file.name]?.error || false;
                                    return (
                                        <div
                                            key={`${file.name}-${file.size}-${file.lastModified}`}
                                            className="flex flex-col rounded-md w-full bg-gray-140 p-3"
                                        >
                                            <div className="flex flex-row w-full items-center mb-2">
                                                <SVGWhiteIcon />
                                                <div className="flex flex-col justify-center ml-3 w-full">
                                                    <div className="flex flex-row justify-between items-center w-full">
                                                        <p className="text-sm font-normal text-white">
                                                            {file.name.length > 50
                                                                ? `${file.name.slice(0, 25)}...${file.name.slice(-25)}`
                                                                : file.name}
                                                        </p>
                                                        {!isUploaded && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCancelFileUpload(file)}
                                                            >
                                                                <XMarkIcon className="h-5 w-5 text-white hover:cursor-pointer hover:text-gray-30" />
                                                            </button>
                                                        )}
                                                        {isUploaded && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDeleteFile(file)}
                                                            >
                                                                <BinIcon className="stroke-red-50" />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <p className="text-xxs text-gray-60">
                                                        {fileSizeUploadedWithUnit} of {fileSizeTotal}
                                                        <span className="text-xs font-bold text-gray-40 mr-2 ml-2">
                                                            &#xB7;
                                                        </span>
                                                        <span
                                                            className={`${error ? 'text-red-60' : isUploaded ? 'text-green-60' : 'text-gray-60'}`}
                                                        >
                                                            {error
                                                                ? 'Upload Error'
                                                                : isUploaded
                                                                  ? 'Completed'
                                                                  : 'Uploading'}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex flex-row h-1.5 bg-gray-100 rounded-lg w-full">
                                                <div
                                                    className="h-1.5 bg-blue-60 rounded-lg"
                                                    style={{
                                                        width: isUploaded ? '100%' : `${progress}%`,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <hr className="mt-2 bg-gray-100 border-gray-100" />
                    <div className="w-full flex flex-row justify-end gap-3 p-6 h-[88px]">
                        <Button
                            title="Cancel"
                            className="hover:bg-gray-160"
                            buttonType={ButtonType.SECONDARY}
                            buttonSize={ButtonSize.LARGE}
                            disabled={isLoading}
                            onClick={handleCancel}
                        />
                        <Button
                            title="Add Frames"
                            buttonType={ButtonType.PRIMARY}
                            buttonSize={ButtonSize.LARGE}
                            isLoading={isAddingFrames}
                            disabled={
                                files.length === 0 ||
                                files.some((file) => !progressMap[file.name]?.isUploaded) ||
                                isLoading
                            }
                            onClick={handleAddFrames}
                        />
                    </div>
                </div>
            </Modal>
            <MissingFontsModal
                fonts={missingFonts}
                uploadedFonts={uploadedFonts}
                onUploadDone={(font) => {
                    setUploadedFonts((prev) => [...prev, font]);
                }}
                isOpen={isMissingFontModalOpen}
                setIsOpen={setIsMissingFontModalOpen}
            />
        </div>
    );
};
